/**
 * Catalogue.tsx — the /tools surface (Stage 4: Tool Discovery & Catalogue Search). Lists the
 * Dataverse-backed tools the viewer may see from GET /api/tools, with a raw search box, ownership
 * scope, category/department filters and a sort control — all driven through the URL query string so
 * the data-router loader re-runs on submit (back/forward restore results). Adds a small "Recently
 * added" list, linked tags, an adoption count and an "adopted by you" marker.
 *
 * PRODUCT LANGUAGE: this is the "Catalogue". Each row links to the tool's detail page, where the
 * adopt action lives. Nothing here says "download"; "clone" appears nowhere user-facing.
 *
 * VISIBILITY & SAFETY: the backend enforces the visibility gate (a non-owner sees only Approved
 * tools; the owner also sees their own Drafts), so this page can never surface another user's Draft.
 * GET /api/tools returns only safe summaries — never clientdata, tokens, cookies, headers. The
 * search term is submitted as a query param and is NOT recorded server-side (AE-F36). This page
 * renders exactly the safe fields.
 *
 * RESULT COUNT & PAGING (Slice B): the backend now returns `total` with each page, so the results
 * view states how many tools matched (DS-F18) and announces it to assistive technology (DS-NFR02).
 * Paging is the `limit`/`offset` pair in the URL, so back/forward restores a page like every other
 * control here. DS §8's "20, then load more" is served as Previous/Next rather than an appending
 * button: the interaction is presentational, and pages keep working past the backend's per-page
 * ceiling, which an ever-growing `limit` would run into.
 */
import { Form, Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import {
  apiGet,
  type CatalogueBuilderOption,
  type CatalogueToolSummary,
  type TagLink,
  type ToolRefData,
} from './api';
// TD-F04: the classification links. Built in one module so this page and the tool page cannot encode
// the same filter two ways — see catalogueLinks.ts for why only ONE of the two carries a label param.
import { catalogueCategoryHref, catalogueHeading, catalogueTagHref } from './catalogueLinks';
import { FeatureDisabled, SignedOut, TOOLS_STYLES } from './shared';
// DS-F07/DS-NFR06: the card's trust signals are worded by the SAME functions the tool page's Trust
// list uses. See trustSignals.ts for which signals a card carries and why the others are not here.
import {
  TRUST_SIGNAL_LABELS,
  formatAdoption,
  reviewMarkerIcon,
  formatConfirmedWorking,
  formatHealth,
  formatOpenIssueCount,
  formatRating,
} from './trustSignals';
import { getLeaderboard, EMPTY_LEADERBOARD_VIEW, type LeaderboardView } from '../leaderboard/api';
import { Top5Section } from '../leaderboard/Top5Section';

/** The search/filter/sort selections read back from the URL, so the controls stay populated. */
interface CatalogueQuery {
  q: string;
  scope: string; // '' | 'all' | 'mine' | 'shared' | 'adopted'
  category: string; // '' or a numeric id as string
  department: string; // '' or a numeric id as string
  /** TD-F04's tag click-through — a BIGINT tag id, as a string. '' when no tag filter is active. */
  tag: string;
  /** Display-only: the tag's label, for the page heading (`tagLabel` is never sent as a filter). */
  tagLabel: string;
  /** DS-F04 builder filter — a BIGINT `builder_user_id` as a string. '' when off. */
  builder: string;
  /** DS-F04 review-status filter: '' | 'approved' | 'unverified' | 'lapsed'. */
  review: string;
  /** DS-F04 minimum average rating: '' | '3' | '4' (DS §8's two options; off by default). */
  minRating: string;
  sort: string; // '' | 'default' | 'newest' | 'recently_updated' | 'most_adopted'
}

/** What the backend reported about the page it actually served (not what we asked for). */
interface CataloguePaging {
  total: number;
  limit: number;
  offset: number;
}

export type CatalogueLoaderData =
  | {
      state: 'ok';
      tools: CatalogueToolSummary[];
      refData: ToolRefData | null;
      /** DS-F04's builder options. `[]` on any failure — the filter hides, the page still works. */
      builders: CatalogueBuilderOption[];
      query: CatalogueQuery;
      paging: CataloguePaging;
      /** The weekly Top 5 SECTION at the top of the page (RT-F01). Fetched NON-FATALLY: an empty view
       * on any failure, so the section self-hides (RT-F06) rather than breaking the catalogue. */
      leaderboard: LeaderboardView;
    }
  | { state: 'signedOut' }
  | { state: 'featureDisabled' };

/**
 * The sort options, mapping the backend value to a human label.
 *
 * `default` is listed with a label that depends on whether a query is present, because that is what
 * the backend does: relevance to the query when there is one (DS-F05), newest-first when there is not
 * (DS §8's default browse state). One option with an honest label beats two that claim to differ.
 *
 * `newest` is offered explicitly so a searcher can force newest-first over relevance — DS-F05 lists
 * it as a user-choosable sort, and without this option it is unreachable from the UI. It is offered
 * ONLY with a query because that is the only case where it differs from `default`; with no query
 * `default` already IS newest-first, and two options that produce the same order is the thing the
 * label above avoids.
 *
 * That only became true when the API stopped ignoring it. `listCatalogueTools` used to compute
 * `useRelevance = tsq !== null && (sort === 'default' || sort === 'newest')`, so this option was
 * offered in exactly the case where it silently kept relevance order and changed nothing on screen.
 * Fixed in db/tool.ts (B18 / DS-F05) — do not put 'newest' back into that condition.
 */
function sortOptions(hasQuery: boolean): { value: string; label: string }[] {
  return [
    { value: 'default', label: hasQuery ? 'Best match (default)' : 'Newest first (default)' },
    ...(hasQuery ? [{ value: 'newest', label: 'Newest first' }] : []),
    { value: 'recently_updated', label: 'Recently updated' },
    { value: 'most_adopted', label: 'Most adopted' },
  ];
}

const SCOPES: { value: string; label: string }[] = [
  { value: 'all', label: 'All tools' },
  { value: 'mine', label: 'Mine' },
  { value: 'shared', label: 'Shared by others' },
  { value: 'adopted', label: 'Adopted by me' },
];

/**
 * GET /api/tools (with the current query string) → the list, plus GET /api/tools/refdata for the
 * category/department dropdowns. A 404 on /api/tools means the feature flag is off. The refdata
 * fetch failing is non-fatal — the dropdowns just fall back to id-free (they degrade, never crash).
 * The search/filter/sort params are read from the request URL (data-router idiom) and forwarded.
 */
export async function catalogueLoader({
  request,
}: LoaderFunctionArgs): Promise<CatalogueLoaderData> {
  const url = new URL(request.url);
  const params = url.searchParams;

  // Read back the raw selections so the form controls can be repopulated.
  const query: CatalogueQuery = {
    q: params.get('q') ?? '',
    scope: params.get('scope') ?? '',
    category: params.get('category') ?? '',
    department: params.get('department') ?? '',
    tag: params.get('tag') ?? '',
    tagLabel: params.get('tagLabel') ?? '',
    builder: params.get('builder') ?? '',
    review: params.get('review') ?? '',
    minRating: params.get('minRating') ?? '',
    sort: params.get('sort') ?? '',
  };

  // Forward only the params that are set, so an empty control doesn't send an empty value.
  // `tagLabel` is deliberately NEVER forwarded — it's display-only, read back into `query.tagLabel`
  // above for the heading, and carried through `pageHref` below so paging doesn't lose it.
  const forward = new URLSearchParams();
  if (query.q.trim()) forward.set('q', query.q.trim());
  if (query.scope) forward.set('scope', query.scope);
  if (query.category) forward.set('category', query.category);
  if (query.department) forward.set('department', query.department);
  if (query.tag) forward.set('tag', query.tag);
  if (query.builder) forward.set('builder', query.builder);
  if (query.review) forward.set('review', query.review);
  if (query.minRating) forward.set('minRating', query.minRating);
  if (query.sort) forward.set('sort', query.sort);
  // `offset` is forwarded verbatim when it parses as a non-negative integer; `limit` is deliberately
  // NOT sent, so the page size stays the platform owner's `results_per_page` setting (PC-F20) rather
  // than something this page decided. A junk offset is dropped, matching the route's permissiveness.
  const offsetParam = Number(params.get('offset'));
  if (Number.isSafeInteger(offsetParam) && offsetParam > 0)
    forward.set('offset', String(offsetParam));
  const qs = forward.toString();

  const res = await apiGet<{
    tools: CatalogueToolSummary[];
    total: number;
    limit: number;
    offset: number;
  }>(`/api/tools${qs ? `?${qs}` : ''}`, { treat404AsFeatureDisabled: true });
  if (res.kind === 'unauthorized') return { state: 'signedOut' };
  if (res.kind === 'featureDisabled') return { state: 'featureDisabled' };
  if (res.kind === 'error') throw new Error(`GET /api/tools failed (${res.status})`);

  // Reference data for the dropdowns, and the builder options for DS-F04's builder filter. Both
  // non-fatal on failure — those filters just show no options. In parallel because neither depends on
  // the other and this page already makes three requests.
  const [refRes, buildersRes] = await Promise.all([
    apiGet<ToolRefData>('/api/tools/refdata'),
    apiGet<CatalogueBuilderOption[]>('/api/tools/builders'),
  ]);
  const refData = refRes.kind === 'ok' ? refRes.body : null;
  const builders = buildersRes.kind === 'ok' ? buildersRes.body : [];

  // The weekly Top 5 SECTION at the top of the catalogue (RT-F01). NON-FATAL, exactly like refdata:
  // any non-OK (or an empty run) degrades to an empty view so the section self-hides (RT-F06) rather
  // than failing the whole page. It is NOT a route — just this page's own leaderboard section.
  const leaderboardRes = await getLeaderboard();
  const leaderboard = leaderboardRes.kind === 'ok' ? leaderboardRes.body : EMPTY_LEADERBOARD_VIEW;

  // The backend reports the page it SERVED (clamped, and defaulted from platform_setting), so paging
  // is computed from that rather than from what this loader asked for.
  const paging: CataloguePaging = {
    total: Number(res.body.total) || 0,
    limit: Number(res.body.limit) || res.body.tools.length,
    offset: Number(res.body.offset) || 0,
  };

  return { state: 'ok', tools: res.body.tools, refData, builders, query, paging, leaderboard };
}

/** The connector requirements, honestly: the api names, or a neutral "none listed". */
function Connectors({ tool }: { tool: CatalogueToolSummary }) {
  if (tool.requiredConnectors.length === 0) {
    return <span className="tb-muted">None listed</span>;
  }
  return <>{tool.requiredConnectors.map((c) => c.apiName).join(', ')}</>;
}

/**
 * The tags, each a LINK into the catalogue filtered to that tag (TD-F04). NOTHING when the tool has
 * none — the em dash this returned was a table cell that could not be empty; a card simply omits the
 * line (its caller tests the same emptiness).
 *
 * `tagLinks`, not the `tags: string[]` this took before: the filter is keyed on the tag id, and the
 * label is a display form that is neither unique nor stable under a CAT-F10 merge. Separators are
 * rendered BETWEEN the links rather than by joining, because a joined string cannot hold anchors — and
 * they are `aria-hidden`, so a screen reader hears a list of tags instead of "triage comma".
 */
function Tags({ tags }: { tags: TagLink[] }) {
  if (tags.length === 0) return null;
  return (
    <>
      {tags.map((t, i) => (
        <span key={t.tagId}>
          {i > 0 ? <span aria-hidden="true">, </span> : null}
          <Link to={catalogueTagHref(t)}>{t.label}</Link>
        </span>
      ))}
    </>
  );
}

/**
 * The trust cell (open publishing). Every visible tool is adoptable regardless of this label — it
 * only tells the viewer whether the tool has been reviewed yet. "Unverified" is muted+bold so an
 * unreviewed-but-available tool reads as distinct from an "Approved" one, without any styling polish.
 */
function TrustLabel({ label }: { label: 'Approved' | 'Unverified' }) {
  if (label === 'Approved') return <span style={{ fontWeight: 600 }}>Trust: Approved</span>;
  return (
    <span className="tb-muted" style={{ fontWeight: 600 }}>
      Trust: Unverified
    </span>
  );
}

/**
 * The search + filter + sort form. A plain <Form method="get"> so submitting rewrites the URL query
 * and the loader re-runs — no client state, no new dependency. Every control's name is the query
 * param the loader reads. defaultValue (not value) keeps them uncontrolled but populated from the URL.
 */
// NOT role="search": DS-F18 says "on the Tools page the global field IS the page's search field —
// there are never two search boxes on one page", so the search box that used to live here is gone and
// the frame's field owns searching. This form is filters and sorts only, which is exactly the division
// DS-F19 asks for. Two role="search" landmarks on one page would also leave a screen-reader user
// choosing between two things both called "search".
function SearchFilters({
  refData,
  builders,
  query,
}: {
  refData: ToolRefData | null;
  builders: CatalogueBuilderOption[];
  query: CatalogueQuery;
}) {
  return (
    <Form method="get" aria-label="Filter and sort the results">
      <fieldset>
        <legend>Narrow these results</legend>
        {/* The query, carried HIDDEN so a filter or sort change composes with the search instead of
            discarding it. <Form method="get"> rebuilds the query string from its own fields, so
            without this a category change would silently drop what the person searched for. It is
            the global field, not this input, that lets them EDIT the query. */}
        <input type="hidden" name="q" value={query.q} />
        {/* Same reasoning for the tag click-through (TD-F04): a filter/sort change must not silently
            drop it. tagLabel rides along purely for the heading; it is never treated as a filter. */}
        <input type="hidden" name="tag" value={query.tag} />
        <input type="hidden" name="tagLabel" value={query.tagLabel} />
        <label className="tb-field">
          <span>Show</span>
          <select name="scope" defaultValue={query.scope || 'all'}>
            {SCOPES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="tb-field">
          <span>Category</span>
          <select name="category" defaultValue={query.category}>
            <option value="">All categories</option>
            {refData?.categories.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="tb-field">
          <span>Department (tool is for)</span>
          <select name="department" defaultValue={query.department}>
            <option value="">All departments</option>
            {refData?.departments.map((d) => (
              <option key={d.id} value={String(d.id)}>
                {d.label}
              </option>
            ))}
          </select>
        </label>
        {/* DS-F04's builder filter. Hidden entirely when the list is empty rather than rendered as an
            empty dropdown: /api/tools/builders lists only builders with a tool this viewer can see, so
            "no options" means there is nothing to narrow, not that something failed. */}
        {builders.length > 0 && (
          <label className="tb-field">
            <span>Builder</span>
            <select name="builder" defaultValue={query.builder}>
              <option value="">Any builder</option>
              {builders.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.label}
                </option>
              ))}
            </select>
          </label>
        )}
        {/* DS-F04's review status. The labels match what the results actually say: a card's trust
            signal reads "Trust: Approved" or "Trust: Unverified" (see TrustLabel above), so the filter
            uses those words instead of inventing a third vocabulary for the same fact. "Review lapsed"
            is a different fact (CAT-F17) — a reviewed tool edited since — not a slice of the other two. */}
        <label className="tb-field">
          <span>Review status</span>
          <select name="review" defaultValue={query.review}>
            <option value="">Any review status</option>
            <option value="approved">Approved</option>
            <option value="unverified">Unverified (not yet reviewed)</option>
            <option value="lapsed">Review lapsed</option>
          </select>
        </label>
        {/* DS §8: "Minimum-rating filter options: 3+ stars · 4+ stars, and off by default" — hence
            exactly two options and "" first. Rating is a FILTER only and never a sort (DS §8, §7.7), so
            there is deliberately no matching entry in sortOptions(). A tool with too few ratings to
            show an average is excluded by this filter and never hidden without it (DS-F04/FB-F21). */}
        <label className="tb-field">
          <span>Minimum rating</span>
          <select name="minRating" defaultValue={query.minRating}>
            <option value="">Any rating</option>
            <option value="3">3+ stars</option>
            <option value="4">4+ stars</option>
          </select>
        </label>
        <label className="tb-field">
          <span>Sort by</span>
          <select name="sort" defaultValue={query.sort || 'default'}>
            {sortOptions(query.q.trim().length > 0).map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        {/* No `offset` input: submitting rewrites the query string from these fields alone, so a new
            search or filter change always lands on the first page rather than deep inside the old one. */}
        <button type="submit">Search</button>{' '}
        {/* A plain link back to the unfiltered catalogue — clears every param. */}
        <Link to="/tools">Clear</Link>
      </fieldset>
    </Form>
  );
}

/**
 * "Recently added" — the newest few tools in the CURRENT (already visibility-gated, already filtered)
 * result set. This is a neutral browse aid (DS-F10), never a ranked chart of winners: it is simply
 * the first N rows, which the backend returns newest-first by default. Hidden when a non-default sort
 * is active (so it doesn't contradict the user's chosen order) or when there are too few tools.
 *
 * Also hidden whenever a QUERY is present, and on any page but the first. Both would make the heading
 * lie: with a query the rows come back in relevance order, not date order, so "the first five" are not
 * the newest anything; past the first page they are neither the newest nor the most relevant.
 */
function RecentlyAdded({
  tools,
  sort,
  hasQuery,
  offset,
}: {
  tools: CatalogueToolSummary[];
  sort: string;
  hasQuery: boolean;
  offset: number;
}) {
  const isDefaultOrder = sort === '' || sort === 'default' || sort === 'newest';
  if (hasQuery || offset > 0) return null;
  if (!isDefaultOrder || tools.length < 2) return null;
  const recent = tools.slice(0, 5);
  return (
    <section aria-labelledby="recent-heading">
      <h2 id="recent-heading" style={{ fontSize: '1rem' }}>
        Recently added
      </h2>
      <ul>
        {recent.map((t) => (
          <li key={t.toolId}>
            <Link to={`/tools/${t.toolId}`}>{t.name}</Link>
            {t.mine ? ' (yours)' : ''}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * ONE RESULT CARD (DS-F07). Carries the "Card" column of `REQUIREMENTS_TOOL_DETAIL_PAGE.md` §8 — in
 * that document's order — with the same value and the same wording as the tool page (DS-NFR06).
 *
 * WHY THIS IS NOT A TABLE ROW. The results were an 11-column table, and a trust signal set does not
 * fit one: §8 hides three of the six card signals when they have nothing to say (health when Healthy,
 * open issues when none, confirmed-working at zero), and a column cannot be conditionally absent for
 * one row. A table forces a value into every cell, so "Healthy" would print on almost every row — which
 * is exactly how a reader learns to stop looking at the column that was meant to warn them.
 *
 * WHAT IT SHOWS, and every one of these is §8's ruling rather than a layout choice:
 *   1  adoption      — always, with TD-F07's absence copy ("Not adopted yet")
 *   2  review marker — an ICON only; the date and the version reviewed stay on the tool page
 *   3  confirmed working, 4 health, 5 open issue COUNT — only when there is something to say
 *   10 rating        — always, with "Not yet rated" when there is no publishable average
 * Signals 6 (version), 8 (time saved) and 9 (likes) are "Card: no", and are not in the list payload at
 * all — so this cannot render them by reaching for a field that happened to be there. Signal 7 (access)
 * is "Card: yes (if unmet)" and cannot arise: the API's access gate removes a tool the viewer may not
 * use from the results and from DS-F18's total together.
 *
 * Everything the table carried is still here — description, owner, ownership, tags, publication status,
 * trust label, connectors, whether a blueprint is stored — as footer metadata below the signals.
 * Nothing was dropped in the conversion; it was reordered by how much of the decision it carries.
 *
 * <h2>, because the cards sit directly under the page's <h1> with no results sub-heading between them.
 */
function ToolCard({ tool }: { tool: CatalogueToolSummary }) {
  // Each formatter answers "is there anything to say?" as well as "what does it say", so absence is a
  // null decided in the one place that knows the rule — never a threshold re-tested here.
  const marker = reviewMarkerIcon(tool.reviewMarker);
  const confirmedWorking = formatConfirmedWorking(tool.recentlyConfirmedWorkingCount);
  const health = formatHealth(tool.healthState);
  const openIssues = formatOpenIssueCount(tool.openIssueCount);

  return (
    <li className="tb-card">
      <div className="tb-card__head">
        <h2 className="tb-card__title">
          <Link to={`/tools/${tool.toolId}`}>{tool.name}</Link>
        </h2>
        {/* Signal 2, icon only. The glyph is decorative; the words are what assistive technology reads,
            and they are the same words the tool page's marker line starts with. */}
        {marker ? (
          <span className="tb-card__marker">
            <span aria-hidden="true">{marker.glyph}</span>
            <span className="sr-only">{marker.label}</span>
          </span>
        ) : null}
      </div>

      <p className="tb-card__desc">{tool.description}</p>
      <p className="tb-card__by">
        {tool.mine ? 'Yours' : `Shared by ${tool.ownerName}`}
        {tool.adoptedByMe ? ' — Adopted by you' : ''}
      </p>

      {/* The §8 signals, in §8's order, in the same <dl> with the same labels as the Trust list. */}
      <dl className="tb-card__signals">
        <dt>{TRUST_SIGNAL_LABELS.adoption}</dt>
        <dd>{formatAdoption(tool)}</dd>

        {confirmedWorking !== null && (
          <>
            <dt>{TRUST_SIGNAL_LABELS.confirmedWorking}</dt>
            <dd>{confirmedWorking}</dd>
          </>
        )}

        {health !== null && (
          <>
            <dt>{TRUST_SIGNAL_LABELS.health}</dt>
            <dd>{health}</dd>
          </>
        )}

        {openIssues !== null && (
          <>
            <dt>{TRUST_SIGNAL_LABELS.openIssues}</dt>
            <dd>{openIssues}</dd>
          </>
        )}

        <dt>{TRUST_SIGNAL_LABELS.rating}</dt>
        <dd>{formatRating(tool)}</dd>
      </dl>

      {/* The classification and provenance the table's remaining columns carried. Below the signals
          because none of it decides whether to open the tool — it describes what the tool is. */}
      <p className="tb-card__meta">
        <span>
          <TrustLabel label={tool.trustLabel} />
        </span>
        <span>{tool.publicationStatus}</span>
        {/* TD-F04: the primary category, as the reader's own way back to "more like this one". The
            department is deliberately NOT linked here — it is the department the tool is FOR, which
            reads as provenance rather than as a filter a browser of the catalogue would reach for,
            and the filter form offers it as a dropdown two inches up the page. */}
        <span>
          <Link to={catalogueCategoryHref(tool.categoryId)}>{tool.categoryLabel}</Link>
        </span>
        {tool.tagLinks.length > 0 ? (
          <span>
            <Tags tags={tool.tagLinks} />
          </span>
        ) : null}
        <span>
          Connectors: <Connectors tool={tool} />
        </span>
        <span>{tool.hasClientdata ? 'Blueprint stored' : 'No blueprint stored'}</span>
      </p>
    </li>
  );
}

/**
 * Build the URL for another page of the CURRENT search, preserving every filter.
 *
 * `offset` is dropped rather than set to 0 for the first page, so page one has one canonical URL
 * instead of two that render identically.
 */
function pageHref(query: CatalogueQuery, offset: number): string {
  const p = new URLSearchParams();
  if (query.q.trim()) p.set('q', query.q.trim());
  if (query.scope) p.set('scope', query.scope);
  if (query.category) p.set('category', query.category);
  if (query.department) p.set('department', query.department);
  if (query.tag) p.set('tag', query.tag);
  if (query.tagLabel) p.set('tagLabel', query.tagLabel);
  if (query.builder) p.set('builder', query.builder);
  if (query.review) p.set('review', query.review);
  if (query.minRating) p.set('minRating', query.minRating);
  if (query.sort) p.set('sort', query.sort);
  if (offset > 0) p.set('offset', String(offset));
  const qs = p.toString();
  return `/tools${qs ? `?${qs}` : ''}`;
}

/**
 * The result count (DS-F18) in an `aria-live` region.
 *
 * DS-NFR02 requires "the result count and any filter change announced to assistive technology". The
 * count sits in a `role="status"` region so re-running the loader (a new search, a filter change, a
 * page step) speaks the new count without moving focus. `aria-live="polite"` because a search result
 * is never an interruption.
 *
 * It states the TOTAL the filters matched, and the slice on screen when that is only part of it —
 * "1–20 of 57" answers both "did my search find anything" and "am I seeing all of it".
 */
function ResultCount({ total, offset, shown }: { total: number; offset: number; shown: number }) {
  const label =
    total === 0
      ? 'No tools match your search.'
      : shown === total
        ? `${total} ${total === 1 ? 'tool' : 'tools'}`
        : `Showing ${offset + 1}–${offset + shown} of ${total} tools`;
  return (
    <p role="status" aria-live="polite" className="tb-muted">
      {label}
    </p>
  );
}

/**
 * Previous / Next for the current search (DS §8: "20, then load more").
 *
 * Plain <Link>s, so paging is a normal navigation the data router already knows how to restore — no
 * client state, matching the rest of this page. Rendered as links only when there IS another page in
 * that direction; a disabled-looking control that does nothing is worse than no control.
 *
 * `limit` is the page size the BACKEND applied, not one this component assumed, so a platform owner
 * changing `results_per_page` moves these steps with it.
 */
function Pager({
  query,
  paging,
  shown,
}: {
  query: CatalogueQuery;
  paging: CataloguePaging;
  shown: number;
}) {
  const { total, limit, offset } = paging;
  const hasPrev = offset > 0;
  const hasNext = offset + shown < total;
  if (!hasPrev && !hasNext) return null;

  // Clamp the previous step at zero so a hand-edited offset cannot produce a negative one.
  const prevOffset = Math.max(0, offset - limit);
  return (
    <nav aria-label="Result pages">
      {hasPrev ? <Link to={pageHref(query, prevOffset)}>← Previous</Link> : null}
      {hasPrev && hasNext ? ' · ' : null}
      {hasNext ? <Link to={pageHref(query, offset + shown)}>Next →</Link> : null}
    </nav>
  );
}

export function Catalogue() {
  const data = useLoaderData() as CatalogueLoaderData;

  if (data.state === 'signedOut') return <SignedOut title="Catalogue" returnTo="/tools" />;
  if (data.state === 'featureDisabled') return <FeatureDisabled title="Catalogue" />;

  const { tools, refData, builders, query, paging, leaderboard } = data;

  return (
    <section aria-labelledby="catalogue-heading" className="tb-tools">
      <style>{TOOLS_STYLES}</style>
      {/* The weekly Top 5 SECTION at the TOP of the catalogue (RT-F01). Self-hides when empty
          (RT-F06), so it is rendered unconditionally above the page heading's controls. */}
      <Top5Section view={leaderboard} />
      {/* TD-F04: a classification click-through titles the page with what it is filtered to, per
          Will's "Catalogue Page with tag query as title" diagram — now for a category link as well,
          since arriving at eight tools under the bare word "Catalogue" reads as a catalogue of eight
          tools. The category's NAME is looked up from the refdata the dropdowns already use rather
          than carried in the URL; catalogueLinks.ts says why that asymmetry with the tag is correct. */}
      <h1 id="catalogue-heading">
        {catalogueHeading({
          tag: query.tag,
          tagLabel: query.tagLabel,
          category: query.category,
          categories: refData?.categories ?? [],
        })}
      </h1>
      <p className="tb-muted" style={{ maxWidth: '60ch' }}>
        Tools your colleagues have shared. Open one to see its details and adopt it into your own
        Power Automate environment.
      </p>
      {query.tag && (
        <p>
          <Link to="/tools">Clear tag filter</Link>
        </p>
      )}

      <SearchFilters refData={refData} builders={builders} query={query} />

      {/* The count comes BEFORE the results so a screen reader hears how many there are first. */}
      <ResultCount total={paging.total} offset={paging.offset} shown={tools.length} />

      <RecentlyAdded
        tools={tools}
        sort={query.sort}
        hasQuery={query.q.trim().length > 0}
        offset={paging.offset}
      />

      {tools.length === 0 ? (
        // ResultCount already states there are none; this is the visible empty state next to the cards.
        <p>Try a shorter search term, or clear the filters.</p>
      ) : (
        // DS-F07: cards, not rows.
        //
        // `role="list"` is redundant in the spec and the linter is right about that — but the card grid
        // sets `list-style: none`, and Safari/VoiceOver drops the list semantics of any <ul> styled that
        // way. That is the one browser where omitting the role silently costs a screen-reader user the
        // "list, 12 items" announcement, which on a results page is the count itself. Re-stating the
        // role is the documented workaround and costs every other browser nothing.
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        <ul className="tb-card-grid" role="list">
          {tools.map((t) => (
            <ToolCard key={t.toolId} tool={t} />
          ))}
        </ul>
      )}

      <Pager query={query} paging={paging} shown={tools.length} />
    </section>
  );
}
